import { useNavigate } from 'react-router'
import { Lock, Mail } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import FormInput from '@/elements/FormInput'
import OtpInput from '@/elements/OtpInput'
import toast from '@/utils/toast'
import useAuthStore from '@/stores/authStore'
import { useTheme } from '@/utils/theme'
import {
  loginWithPassword,
  respondToChallenge,
  type AuthChallengeResponse
} from '@/api/auth'

type ViewState = 'login' | 'mfa'

interface EmailPasswordLoginProps {
  firstTimeLogin?: boolean
  onError?: (message: string) => void
  flashError?: boolean
}

function EmailPasswordLogin({ firstTimeLogin = false, onError, flashError = false }: EmailPasswordLoginProps) {
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [viewState, setViewState] = useState<ViewState>('login')
  const [challengeData, setChallengeData] = useState<AuthChallengeResponse | null>(null)

  const { theme } = useTheme()
  const setCredentials = useAuthStore(state => state.setCredentials)
  const initialize = useAuthStore(state => state.initialize)

  const handleLogin = async () => {
    setLoading(true)
    try {
      const result = await loginWithPassword(email, password)

      if (result.type === 'authenticated') {
        setCredentials({
          access_token: result.tokens.accessToken,
          id_token: result.tokens.idToken,
          refresh_token: result.tokens.refreshToken,
          expires_in: result.tokens.expiresIn,
          token_type: 'Bearer'
        })
        await initialize(false, undefined, email)
      } else {
        setChallengeData(result)
        setViewState('mfa')
        toast.info('Please enter the verification code sent to your email')
      }
    } catch {
      onError?.('Incorrect email or password.')
    } finally {
      setLoading(false)
    }
  }

  const handleMfaSubmit = async (codeOverride?: string) => {
    if (!challengeData) return
    const code = codeOverride ?? mfaCode

    setLoading(true)
    try {
      const result = await respondToChallenge({
        challengeName: challengeData.challengeName,
        session: challengeData.session,
        username: challengeData.username,
        code,
      })

      if (result.type !== 'authenticated') {
        onError?.('Verification failed')
        return
      }

      setCredentials({
        access_token: result.tokens.accessToken,
        id_token: result.tokens.idToken,
        refresh_token: result.tokens.refreshToken,
        expires_in: result.tokens.expiresIn,
        token_type: 'Bearer'
      })
      await initialize(false, undefined, email)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Verification failed'
      onError?.(message)
    } finally {
      setLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setViewState('login')
    setChallengeData(null)
    setMfaCode('')
  }

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (firstTimeLogin) {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error('Please enter a valid email address')
        return
      }
      navigate(`/set-password?t=${btoa(email)}`)
    } else {
      handleLogin()
    }
  }

  if (viewState === 'mfa') {
    return (
      <form onSubmit={e => { e.preventDefault(); handleMfaSubmit() }} style={{ width: '100%' }}>
        <Flex gap={3} wfull>
          <OtpInput
            label='Verification Code'
            instructions='Enter the 6-digit code sent to your email'
            value={mfaCode}
            onChange={code => {
              setMfaCode(code)
              if (code.length === 6) handleMfaSubmit(code)
            }}
            autoFocus
            disabled={loading}
          />
          <Flex wfull gap={2}>
            <Button submit variant='accent' disabled={loading || mfaCode.length < 6} fullWidth>
              {loading ? 'Verifying...' : 'Verify and Log In'}
            </Button>
            <Button
              variant={theme === 'dark' ? 'secondary' : 'primary'}
              onClick={handleBackToLogin}
              disabled={loading}
              fullWidth
            >
              Cancel
            </Button>
          </Flex>
        </Flex>
      </form>
    )
  }

  return (
    <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
      <Flex gap={3} wfull>
        <FormInput
          type='email'
          label='Email'
          placeholder='Enter your email'
          value={email}
          onChange={e => setEmail(e.target.value)}
          style={{ width: '100%' }}
          Icon={Mail}
          hasError={flashError}
          autoComplete='email'
        />
        <Flex wfull align='start' gap={2}>
          {!firstTimeLogin ? (
            <Flex wfull gap={1}>
              <FormInput
                label='Password'
                type='password'
                placeholder='Enter your password'
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{ width: '100%' }}
                Icon={Lock}
                hasError={flashError}
                autoComplete='current-password'
              />
              <Flex wfull inline justify='flex-end'>
                <Button
                  variant='textOnly'
                  onClick={() => navigate('/reset-password')}
                >
                  Reset Password
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Button
              submit
              variant='accent'
              disabled={!email}
              fullWidth
            >
              Set up your password
            </Button>
          )}
        </Flex>
        {!firstTimeLogin && (
          <Flex wfull py={2} gap={2}>
            <Button submit variant='accent' disabled={loading} fullWidth>
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
          </Flex>
        )}
      </Flex>
    </form>
  )
}

export default EmailPasswordLogin
